import type { LocalNoteData } from "@/types/note";

const MARKDOWN_BLOB_IMAGE_PATTERN = /!\[[^\]]*]\(blob:[^)]+\)/;
const HTML_BLOB_IMAGE_PATTERN = /<img\b[^>]*\bsrc=(["'])blob:[^"']+\1[^>]*>/i;

export function getNoteStorageKey(userId: string, pagePath: string): string {
  const encodedPath = pagePath.replace(/\//g, "_").replace(/^_/, "");
  return `note_${userId}_${encodedPath}`;
}

/** 필기 초기화 이전의 초안(저장 시각 없는 레거시 초안 포함)은 다시 복원하지 않는다. */
export function isNoteDraftBeforeReset(note: LocalNoteData | null, resetAt?: string | null): boolean {
  const resetTime = timestamp(resetAt);
  if (!note || resetTime === null) return false;
  const savedTime = timestamp(note.lastLocalSaved);
  return savedTime === null || savedTime <= resetTime;
}

export function clearLocalNoteDrafts(storage: Storage, userId: string): void {
  const prefix = `note_${userId}_`;
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (key?.startsWith(prefix)) storage.removeItem(key);
  }
}

function timestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

export function noteContentHasBlobImageRef(content: string | null | undefined): boolean {
  if (!content?.includes("blob:")) return false;
  return MARKDOWN_BLOB_IMAGE_PATTERN.test(content) || HTML_BLOB_IMAGE_PATTERN.test(content);
}

export function parseLocalNoteData(raw: string | null): LocalNoteData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const record = parsed as Record<string, unknown>;
      return {
        content: typeof record.content === "string" ? record.content : "",
        lastLocalSaved:
          typeof record.lastLocalSaved === "string" ? record.lastLocalSaved : undefined,
      };
    }
  } catch {
    return { content: raw };
  }
  return { content: raw };
}

export function shouldUseFirestoreNoteContent({
  localContent,
  localSavedAt,
  firestoreContent,
  firestoreSavedAt,
}: {
  localContent?: string | null;
  localSavedAt?: string | null;
  firestoreContent?: string | null;
  firestoreSavedAt?: string | null;
}): boolean {
  if (!firestoreContent) return false;
  if (!localContent) return true;

  if (
    noteContentHasBlobImageRef(localContent) &&
    !noteContentHasBlobImageRef(firestoreContent)
  ) {
    return true;
  }

  const localTime = timestamp(localSavedAt);
  const firestoreTime = timestamp(firestoreSavedAt);
  if (localTime === null || firestoreTime === null) return false;

  return firestoreTime >= localTime;
}
